import { useEffect } from "react";
import { useGetOne } from "ra-core";
import { useWatch } from "react-hook-form";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import { isValidRecordId } from "@/lib/isValidRecordId";
import type { Contact } from "@/components/atomic-crm/types";
import { contactHasSmsPhone } from "@/modules/messages/messageContactUtils";
import { useMessagingEnabled } from "@/modules/messages/useMessagingEnabled";

/**
 * "Notify client" checkbox for create forms. Reads the form's `contact_id`
 * and only enables itself when that contact can receive SMS. Hidden when the
 * company has no SMS.
 */
export const ClientSmsNotifyOption = ({
  checked,
  onCheckedChange,
  disabled,
}: {
  checked: boolean;
  onCheckedChange: (value: boolean) => void;
  disabled?: boolean;
}) => {
  const contactId = useWatch({ name: "contact_id" });
  const selectedContactId = isValidRecordId(contactId)
    ? Number(contactId)
    : null;
  const { smsEnabled, isPending: messagingPending } = useMessagingEnabled();
  const { data: contact } = useGetOne<Contact>(
    "contacts",
    { id: selectedContactId as number },
    { enabled: selectedContactId != null },
  );

  const canSms =
    !messagingPending &&
    smsEnabled &&
    contact != null &&
    contactHasSmsPhone(contact);

  useEffect(() => {
    if (!canSms && checked) onCheckedChange(false);
  }, [canSms, checked, onCheckedChange]);

  // No SMS in this company (Messages off or not set up): nothing to offer.
  if (messagingPending || !smsEnabled) return null;

  return (
    <label
      className={cn("flex items-center gap-2 text-sm", !canSms && "opacity-70")}
    >
      <Checkbox
        checked={checked && canSms}
        disabled={disabled || !canSms}
        onCheckedChange={(value) => onCheckedChange(value === true)}
      />
      <span className="min-w-0">
        <span className="font-medium">Notify client</span>
        {!canSms ? (
          <span className="ml-1 text-xs text-muted-foreground">
            {selectedContactId == null
              ? "(select a contact with a phone)"
              : "(needs phone + SMS enabled)"}
          </span>
        ) : null}
      </span>
    </label>
  );
};
